/**
 * CURR-01/CURR-03: the USD_KES rate read.
 *
 * Split out of lib/currency.ts so that file stays pure. This module imports
 * ./client, which evaluates `import.meta.env` at module load and therefore
 * cannot be imported under plain Node — the same reason lib/api/syndicatedApi.ts
 * sits apart from lib/api/syndicatedQuery.ts. Keeping the rules (conversion,
 * rounding, freshness) on the pure side is what makes them testable; this side
 * only fetches.
 */

import { apiGet } from './client';
import { CURRENCY_PAIR, type ExchangeRate } from '../currency';

interface ExchangeRateRow {
  rate: number | string | null;
  fetched_at: string | null;
}

/**
 * Reads the single USD_KES row. `anon` and `authenticated` both hold a direct
 * SELECT grant plus an RLS SELECT policy on this table, so this is a plain
 * table read — no view and no RPC involved.
 *
 * Returns null on any failure, missing row, or unusable value; the caller
 * treats null as "no USD available" and falls back to KES-only.
 */
export async function fetchExchangeRate(): Promise<ExchangeRate | null> {
  const { data, error } = await apiGet<ExchangeRateRow[]>(
    'exchange_rates',
    `currency_pair=eq.${CURRENCY_PAIR}&select=rate,fetched_at&limit=1`
  );

  if (error || !data || data.length === 0) return null;

  const row = data[0];
  // PostgREST returns `numeric` as a string in some configurations, so coerce
  // rather than trusting the runtime type.
  const rate = typeof row.rate === 'string' ? Number(row.rate) : row.rate;

  if (rate === null || !Number.isFinite(rate) || rate <= 0) return null;
  if (!row.fetched_at) return null;

  return { rate, fetchedAt: row.fetched_at };
}
